<script>

	import { createEventDispatcher, onDestroy, onMount } from "svelte";
	import { get } from "svelte/store";
	
	import { device_permissions, selected_devices, setSelectedDevice, two_col_mode, landscape_mode } from "../../stores.js";

	import PlaybackCanvas from "./PlaybackCanvas.svelte";
	import Tiles from "./Tiles.svelte";
	import Toolbar from "./Toolbar.svelte";
	import Chat from "./Chat.svelte";
	import BestAudioModal from "./BestAudioModal.svelte";

	import { Peers, Peers_Handler } from "../../Peers.js";
	import { Devices } from "../../Devices.js";
	import { Dispatcher} from "../../Dispatcher.js";
    import { OutputConfig } from "../../OutputConfig.js";
    import { Logger } from "../../Logger.js";

	// -- Conference disabled (account/DB-driven): hide tiles + chat, show peer-name labels instead.
	const no_conf = OutputConfig.session.disable_conf;

	// -- Landscape mode (canvas fills the screen, tiles hidden, tap-to-reveal toolbar) comes on by itself
	// -- on phones only. A tablet in landscape has room for two-col (and often stands on its case), so
	// -- there, as on desktop, it's the fullscreen button's call.
	const is_phone = OutputConfig.session.browser.isPhone;
	let fullscreen = false;

  	$: twoColMode = $two_col_mode;
	
	let left_col, right_col;
	let main_container;
	let toolbar_wrapper;
	let tiles_container;
	
	//-- Device/Toolbar states
	let camera_muted = false;
	let mic_muted = false;
	let mon_muted = true;
	let open_chat = false;
	let best_audio_open = false;

	let show_toolbar_overlay = false;
	let overlay_timer = null;

	let toolbar_ro = null;
	let toolbar_h = 0;

	//-- 
	const TILES_MIN_HEIGHT = 90;
	const ASPECT_RATIO = 16 / 9;
	const TOOLBAR_HEIGHT = 72 + (20 * 2) + 5; 
	const MAIN_CONTAINER_PADDING = 24 * 2;
	const LANDSCAPE_PADDING_AND_BORDER = 2 + 2 + 0.5 + 0.5; //5 is the padding + border width for top and bottom in landscape mode. 2px top,bottm padding, 0.5 top,bottom border width

	const dispatch = createEventDispatcher();

	
	function isDeviceInLandscape(){
		
		if (!OutputConfig.session.browser.isMobile)
			return false;
		
		// Prefer CSS media query; broadly supported including iOS Safari
		if (window.matchMedia){
			const mql = window.matchMedia("(orientation: landscape)");
			if (mql && typeof mql.matches === "boolean") return mql.matches;
		}
		// Fallback to Screen Orientation API when available
		if (window.screen && window.screen.orientation && typeof window.screen.orientation.type === "string"){
			return window.screen.orientation.type.startsWith("landscape");
		}
		
		// -- maybe get rid of this?
		// Last resort: compare viewport dimensions
		return window.innerWidth > window.innerHeight;
	}
	
	// -- we can also calculate a more continous value for tile max-height, since right now we are just snapping from 160px to 90px based on some thresh for window.innerHeight
	// -- (this is located in breakpoints.scss)
	function recomputeLayout() {

		const canvas_section = document.getElementById("canvas-section");
		// -- NOTE: tiles_container is intentionally NOT required: it doesn't render in no-conf mode,
		// -- and gating on it would make this early-return forever (canvas would never get sized).
		if (!main_container || !left_col || !toolbar_wrapper || !canvas_section) return;

		const mc_rect = main_container.getBoundingClientRect();
		const tw_rect = toolbar_wrapper.getBoundingClientRect();

		const mc_width = mc_rect.width;
		const mc_height = mc_rect.height;
		
		// -- two column calculation from total available width
		const fraction = mc_width / 3.4;
		const two_col_tile_width = fraction;
		const two_col_canvas_width = fraction * 2.4;

		// -- one column calculation from total available height
		// -- this is really (max availale height )
		// -- No-conf renders no tiles, so don't reserve TILES_MIN_HEIGHT for them.
		const one_col_canvas_height = mc_height - (no_conf ? 0 : TILES_MIN_HEIGHT) - tw_rect.height;

		// -- likewise this is widest width for the given height, which is why we divde by aspect ratio
		const one_col_canvas_width = one_col_canvas_height / ASPECT_RATIO;

		// -- No-conf has no right-column content (tiles/chat are conference-gated): stay one-col, full width.
		const tcm = no_conf ? false : ((two_col_canvas_width >= one_col_canvas_width) && (two_col_tile_width > 300));
		two_col_mode.set(tcm);

		// -- defaults to one_col values
		let render_height = one_col_canvas_height;
		let _landscape_mode = false;
		
		// -- render height has to be adapted to two column mode.
		if (tcm){
			render_height = two_col_canvas_width / ASPECT_RATIO;
			// -- measured: the best-audio button can wrap the toolbar onto a second row
			const toolbar_height = Math.max(TOOLBAR_HEIGHT, tw_rect.height);
			const two_col_max_height = window.innerHeight - (toolbar_height + MAIN_CONTAINER_PADDING);
			if (render_height >= two_col_max_height)
				_landscape_mode = true;
			// -- Tablets and desktop stay two-col on short windows (landscape is phone-gated below), so the
			// -- canvas must clamp to what fits vertically; it letterboxes inside the column instead of overflowing.
			render_height = Math.min(render_height, two_col_max_height);
		}
		
		// -- we use a different heuristic to check for _landscape_mode in one column mode
		// -- This heuristic is tile-driven; skip it in no-conf and rely on device orientation only.
		else if (!no_conf) {

			// -- this is actually different than one_col_canvas_height above since that is not the final height, but rather currently avail max_height 
			const canvas_section_height = canvas_section.getBoundingClientRect().height;
			const left_col_height = left_col.getBoundingClientRect().height;
			const tile_height = left_col_height - canvas_section_height - TOOLBAR_HEIGHT;
			if (tile_height < TILES_MIN_HEIGHT * 0.8)
				_landscape_mode = true;

			//console.log("[PL-Session] Landscape Mode: ", _landscape_mode, tile_height);
		}
			
		// -- include physical device orientation as an additional criterion
		// -- Automatic landscape is phone-only: the size heuristics above can misfire on short desktop
		// -- viewports (e.g. 1408x707 MacBook), and tablets have room for two-col. Elsewhere it's the button's.
		_landscape_mode = is_phone ? (_landscape_mode || isDeviceInLandscape()) : fullscreen;
		landscape_mode.set(_landscape_mode);
		
		// -- recalculate height for landscape mode. two_col_mode stands: landscape hides the right column
		// -- instead of unmounting it, so going fullscreen doesn't rebuild every tile (and its audio element)
		if (_landscape_mode)
			render_height = mc_height - LANDSCAPE_PADDING_AND_BORDER;
	
		// -- child components can see this variable in their css when it is updated
		left_col.style.setProperty('--pc-max-h', `${Math.max(0, Math.floor(render_height))}px`);
		
	}

		const selectedDevices = get(selected_devices);

		const unsub_cam_mute = selectedDevices.camera.muted.subscribe(end_state => {
			camera_muted = end_state;
	    })

		const unsub_mic_mute = selectedDevices.microphone.muted.subscribe(end_state => {
			mic_muted = end_state;
	    })

		const unsub_mon_mute = selectedDevices.stream.muted.subscribe(end_state => {
			mon_muted = end_state;
	    })


	/*
	 Note that this is only ever called once, since Session remains mounted even if we go back to StartPanel
	*/
	onMount(async () => {
	

		if (get(two_col_mode))
			open_chat = false;

		recomputeLayout();

		window.addEventListener("orientationchange", recomputeLayout);
		window.addEventListener("resize", recomputeLayout);
		document.addEventListener("fullscreenchange", onBrowserFullscreenChange);
		document.addEventListener("webkitfullscreenchange", onBrowserFullscreenChange);

		// -- the toolbar can gain/lose a row (best-audio button) without any window resize
		toolbar_ro = new ResizeObserver(([entry]) => {
			const h = entry.contentRect.height;
			if (h === toolbar_h) return;
			toolbar_h = h;
			recomputeLayout();
		});
		toolbar_ro.observe(toolbar_wrapper);

	})

	function revealToolbarOverlay(){
		show_toolbar_overlay = true;
		clearTimeout(overlay_timer);
		overlay_timer = setTimeout(() => {
			show_toolbar_overlay = false;
			overlay_timer = null;
		}, 3000);
	}

	function hideToolbarOverlay(){
		show_toolbar_overlay = false;
		clearTimeout(overlay_timer);
		overlay_timer = null;
	}

	// -- currently clicking on the toolbar buttons closes the toolbar because this function just gets rerun
	function handleScreenTap() {
		
		if (!get(landscape_mode)) return;
		
		if (show_toolbar_overlay)
			hideToolbarOverlay();
		else
			revealToolbarOverlay();
	}

	// -- a mouse (desktop, an iPad's trackpad) brings the toolbar up just by moving, like a video player
	function handlePointerMove(e){
		if (e.pointerType === "mouse" && $landscape_mode)
			revealToolbarOverlay();
	}

	// -- The fullscreen view is landscape mode on demand. Where the Fullscreen API exists (desktop, iPad
	// -- Safari, Android; not iPhone) it takes the browser fullscreen too, best effort: the view works
	// -- without it. Runs inside the click itself, since the API wants a user gesture.
	function setFullscreen(on){
		fullscreen = on;
		// -- the overlay starts afresh: an entering click goes on to handleScreenTap, which reveals it
		hideToolbarOverlay();
		if (on)
			requestBrowserFullscreen();
		else
			exitBrowserFullscreen();
		recomputeLayout();
	}

	function browserFullscreenElement(){
		return document.fullscreenElement ?? document.webkitFullscreenElement ?? null;
	}

	// -- the whole page, not just the session: the start panel and App-level alerts stay reachable
	function requestBrowserFullscreen(){
		const root = document.documentElement;
		const request = root.requestFullscreen ?? root.webkitRequestFullscreen;
		const enabled = document.fullscreenEnabled ?? document.webkitFullscreenEnabled;
		if (!enabled || !request || browserFullscreenElement()) return;
		try {
			// -- the unprefixed call returns a promise, the webkit one nothing
			request.call(root)?.catch(e => console.warn("[PL-Session] Fullscreen refused: ", e));
		}
		catch (e){
			console.warn("[PL-Session] Fullscreen refused: ", e);
		}
	}

	function exitBrowserFullscreen(){
		const exit = document.exitFullscreen ?? document.webkitExitFullscreen;
		if (!exit || !browserFullscreenElement()) return;
		try {
			exit.call(document)?.catch(e => console.warn("[PL-Session] Fullscreen exit failed: ", e));
		}
		catch (e){
			console.warn("[PL-Session] Fullscreen exit failed: ", e);
		}
	}

	// -- keeps the view and the browser fullscreen together: leaving it the browser's way (Esc, the iPad's
	// -- X or swipe down) leaves the view too, and one granted after the view already closed (a quick
	// -- double tap on the button) is handed back
	function onBrowserFullscreenChange(){
		const fs_element = browserFullscreenElement();
		if (fullscreen && !fs_element)
			setFullscreen(false);
		else if (!fullscreen && fs_element === document.documentElement)
			exitBrowserFullscreen();
	}

	async function handleClick(type){

		if (type === "snd/rcv monitor"){
			// -- all peers can mute the local stream monitor; only eng can route stream output (see Mixer.setOutputDevice).
			// -- The awp gain reacts to this store via Mixer's stream.muted subscription.
			setSelectedDevice("stream", {muted: !mon_muted});
			return;
		}

		if (type === "hangup"){
			// -- hangup IS a reload; mark it so log analysis doesn't read normal exits as
			// -- frustrated refreshes (this was skewing session forensics)
			Logger.log("Page Load", "info", "App Reload", {reason: "hangup"});
			Logger.flush(true);
			window.location.reload();
			return;
		}

		if (type === "backdrop"){
			open_chat = false;
			return;
		}
		
		if (type === "settings"){
			best_audio_open = false;
			dispatch("open_start_panel");
			return;
		}

		if (type === "best audio"){
			best_audio_open = true;
			return;
		}

		if (type === "chat"){
			open_chat = !open_chat;
			return;
		}
		
		if (type === "fullscreen"){
			setFullscreen(!fullscreen);
			return;
		}
		
		/*
		 TO DO -- subscribe to these values and cache them.
		*/
		if ((type === "camera" || type === "microphone") && get(device_permissions)[type] !== "granted"){
			return;
		}

		Devices.toggleInputMute(type).catch(e => {
			console.error(e);
		})

	}

	onDestroy(() => {
		unsub_cam_mute();
		unsub_mic_mute();
		unsub_mon_mute();
		window.removeEventListener("orientationchange", recomputeLayout);
		window.removeEventListener("resize", recomputeLayout);
		document.removeEventListener("fullscreenchange", onBrowserFullscreenChange);
		document.removeEventListener("webkitfullscreenchange", onBrowserFullscreenChange);
		toolbar_ro?.disconnect();
	})

</script>

            
<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div class="page"> 
	<div class="main-container" bind:this={main_container} class:two-col={twoColMode} class:no-conf={no_conf} class:landscape-mode={$landscape_mode} on:click={handleScreenTap} on:pointermove={handlePointerMove}>
		<div bind:this={left_col} class="left_col" class:two-col={twoColMode}>
			
			<!-- <Heading/> -->
			<PlaybackCanvas/>

			{#if !$two_col_mode && !no_conf}
				<div bind:this={tiles_container} class="tiles-container one-col">
					<Tiles hide={$landscape_mode} peers={$Peers}/>
				</div>
			{/if}

			<div bind:this={toolbar_wrapper} class="toolbar-wrap" class:overlay={$landscape_mode} class:hidden={$landscape_mode && !show_toolbar_overlay}>
				<Toolbar
					handleClick={handleClick}
					micMuted={mic_muted}
					cameraMuted={camera_muted}
					monitorMuted={mon_muted}
					openChat={open_chat}
					isFullscreen={fullscreen}
				/>
			</div>

			<!-- One column chat does not open in landscape mode? -->
			{#if !$two_col_mode && !$landscape_mode && !no_conf && open_chat}
				<Chat handleClick={handleClick}/>
			{/if}
		</div>

		<!-- Hidden, not unmounted, in landscape mode: going fullscreen from two-col keeps its tiles (and their audio) up -->
		{#if $two_col_mode}
			<div bind:this={right_col} class="right_col" class:two-col={$two_col_mode} class:hidden={$landscape_mode}>
				<div bind:this={tiles_container} class="tiles-container">
					<Tiles peers={$Peers}/>
				</div>
				{#if open_chat}
					<Chat handleClick={handleClick}/>
				{/if}
			</div>
		{/if}

		{#if !$two_col_mode && !$landscape_mode && !no_conf && open_chat}
			<div class="backdrop" on:click={() => handleClick("backdrop")}></div>
		{/if}
		
	</div>

	<BestAudioModal bind:open={best_audio_open}/>
</div>

<style lang="scss">

	@import "../styles/main.scss";
	@import "../styles/variables.scss";
	
	.page {
		align-items: center;
		min-width: 320px;
	}

	.main-container {

		// @include pagePadding();
		height: 100vh;
		overflow: hidden;
		grid-template-columns: 1fr;
		display: grid;
		gap: 12px;
		padding: $space-sm;
		position: relative;

		&.two-col {	
			grid-template-columns: 2.4fr 1fr;
		}

		&.landscape-mode{
			// -- a little air at the sides, where the canvas meets the screen edge (width-bound: tablets,
			// -- desktop). Top and bottom stay tight for phones, where it's height-bound (LANDSCAPE_PADDING_AND_BORDER)
			padding: 2px $space-sm;
			grid-template-columns: 1fr;	// -- a two-col right column is hidden, not gone

			// -- centers the canvas where the screen is taller than 16:9 (tablets, desktop). Auto margins
			// -- drop to 0 where it fills the height (phones), so it never clips there
			:global(#canvas-section){
				margin-block: auto;
			}
		}
	}
  
	.left_col {
		position: relative;
		overflow: hidden;
		display: flex;
		flex-direction: column;
		// display: grid;
		// grid-template-rows: auto 1fr auto;
		gap: 5px;
		width: 100%;		
	}

	.tiles-container {
		min-height: 0;
		max-height: 100vh;
	}

	.tiles-container.one-col {
		max-height: 100%;
		@include smoothScrollbar();
	}

	.right_col {
		
		display: none;
		&.two-col {
			position: relative;
			display: flex;
			flex-direction: column;
			height: 100%;
			overflow: hidden;
			gap: $space-lg;
		}
		&.hidden {
			display: none;
		}
	}

	.toolbar-wrap.overlay {
        
		position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 5;
        pointer-events: auto; //allow taps through when hidden
        
        // -- a backing so the toolbar reads over the picture, which it often straddles the edge of.
        // -- :global since .toolbar lives in Toolbar.svelte. No pointer-events here: an explicit auto
        // -- would beat the wrapper's none and leave the hidden buttons tappable
        :global(.toolbar) {
            background: rgba($background-color, 0.85);
            border-radius: $rounded-md;
        }
        
        &.hidden {
            opacity: 0;
            transition: opacity 200ms ease;
			pointer-events: none;
        }
    }


	.backdrop {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		z-index: 1;
	}
  
	
  </style>

